import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useUser } from "@/contexts/UserContext";

interface Profile {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface Props {
  routeId: string;
  routeName: string;
  routeStops: { venueName: string }[];
  onClose: () => void;
}

export default function RouteInviteModal({ routeId, routeName, routeStops, onClose }: Props) {
  const { user } = useUser();
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState<Profile[]>([]);
  const [followers, setFollowers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const fetchFollowers = async () => {
      const { data } = await supabase
        .from("follows")
        .select("follower_id")
        .eq("following_id", user.id)
        .limit(20);
      if (!data || data.length === 0) return;
      const ids = data.map((r: { follower_id: string }) => r.follower_id);
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, username, display_name, avatar_url")
        .in("id", ids);
      setFollowers((profiles ?? []) as Profile[]);
    };
    fetchFollowers();
  }, [user]);

  const handleSearch = async (q: string) => {
    setSearchQ(q);
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    const { data } = await supabase
      .from("profiles")
      .select("id, username, display_name, avatar_url")
      .ilike("username", `%${q.trim()}%`)
      .neq("id", user?.id ?? "")
      .limit(8);
    setResults((data ?? []) as Profile[]);
    setLoading(false);
  };

  const handleInvite = async (target: Profile) => {
    if (!user) return;
    setSending(target.id);

    // Insert into route_invites table — proper join system
    await supabase.from("route_invites").insert({
      route_id: routeId,
      inviter_id: user.id,
      invitee_id: target.id,
      status: "pending",
    });

    setSentTo((prev) => new Set(prev).add(target.id));
    setSending(null);
  };

  const displayList = searchQ.trim().length >= 2 ? results : followers;

  const avatarEl = (p: Profile) => {
    const name = p.display_name || p.username;
    return p.avatar_url ? (
      <img src={p.avatar_url} alt={name} className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0" />
    ) : (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center flex-shrink-0">
        <span className="text-white font-bold text-sm">{name.charAt(0).toUpperCase()}</span>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div className="w-full max-w-md bg-white rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-stone-100">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-bold text-stone-900 text-base">Rotaya Davet Et</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-700 hover:bg-stone-100 rounded-xl cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-xl">
            <i className="ri-route-line text-emerald-600 text-sm" />
            <span className="text-xs text-emerald-700 font-medium truncate">{routeName}</span>
            <span className="text-xs text-emerald-500 ml-auto flex-shrink-0">{routeStops.length} durak</span>
          </div>
          <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
            <i className="ri-information-line text-amber-500 text-sm mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700 leading-relaxed">
              Davet kabul edildiğinde rota her iki profilinizde <strong>birlikte yapıldı</strong> olarak görünür.
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="px-5 pt-4 pb-2">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
            <input
              value={searchQ}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Kullanıcı ara..."
              className="w-full pl-8 pr-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 transition-colors"
            />
            {loading && (
              <i className="ri-loader-4-line animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
            )}
          </div>
          {searchQ.trim().length < 2 && followers.length > 0 && (
            <p className="text-xs text-stone-400 mt-2 mb-1">Takipçileriniz</p>
          )}
          {searchQ.trim().length >= 2 && results.length === 0 && !loading && (
            <p className="text-xs text-stone-400 mt-2">Kullanıcı bulunamadı.</p>
          )}
        </div>

        {/* List */}
        <div className="overflow-y-auto px-5 pb-5" style={{ maxHeight: 300 }}>
          {displayList.length === 0 && searchQ.trim().length < 2 && (
            <div className="text-center py-8 text-stone-400 text-sm">
              <i className="ri-user-line text-3xl text-stone-200 block mb-2" />
              Yukarıdan kullanıcı arayın
            </div>
          )}
          <div className="space-y-2 mt-1">
            {displayList.map((p) => {
              const name = p.display_name || p.username;
              const isSent = sentTo.has(p.id);
              const isSending = sending === p.id;
              return (
                <div
                  key={p.id}
                  className="flex items-center gap-3 p-3 rounded-xl bg-stone-50 border border-stone-100"
                >
                  {avatarEl(p)}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-stone-900 text-sm truncate">{name}</p>
                    <p className="text-xs text-stone-400">@{p.username}</p>
                  </div>
                  <button
                    onClick={() => !isSent && handleInvite(p)}
                    disabled={isSent || isSending}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors flex-shrink-0 cursor-pointer ${
                      isSent
                        ? "bg-emerald-100 text-emerald-700 cursor-default"
                        : "bg-emerald-700 hover:bg-emerald-800 text-white"
                    } disabled:opacity-70`}
                  >
                    {isSending ? (
                      <><i className="ri-loader-4-line animate-spin" /> Gönderiliyor</>
                    ) : isSent ? (
                      <><i className="ri-check-line" /> Gönderildi</>
                    ) : (
                      <><i className="ri-user-add-line" /> Davet Et</>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
